import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth, useStore } from "@/App";
import { pingLive } from "@/hooks/useLive";

// every successful write (from any page or panel) → open pages reload themselves, no refresh button needed
const live = (p) => (r) => { if (!/\/pulse$/.test(p)) setTimeout(() => pingLive({ source: "self", path: p }), 50); return r; };

export const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => (typeof x === "string" ? x : `${(x.loc || []).slice(-1)[0] || "field"}: ${x.msg || "invalid"}`)).join("; ");
  if (d && typeof d === "object") return d.msg || d.message || JSON.stringify(d);
  return e?.message || "Something went wrong";
};

export const money = (v) => {
  const n = Number(v || 0);
  return (n < 0 ? "-$" : "$") + Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/* Cash difference wording: negative = Short, positive = Over, zero = Balanced (never the vague "Over/Short") */
export const osLabel = (v) => { const n = Number(v || 0); return Math.abs(n) < 0.005 ? "Balanced" : n < 0 ? "Short" : "Over"; };
export const osText = (v) => { const n = Number(v || 0); return Math.abs(n) < 0.005 ? "Balanced" : `${osLabel(n)} ${money(Math.abs(n))}`; };
export const osClass = (v) => { const n = Number(v || 0); return Math.abs(n) < 0.005 ? "text-emerald-700" : n < 0 ? "text-red-600" : "text-amber-700"; };

export const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

export const useLegacyApi = () => {
  const { token } = useAuth();
  const { selectedStore } = useStore();
  const storeId = selectedStore?.id;
  return useMemo(() => {
    const base = `${API}/legacy/stores/${storeId}`;
    const headers = { Authorization: `Bearer ${token}` };
    return {
      storeId,
      get: (p, params) => axios.get(`${base}${p}`, { headers, params }).then((r) => r.data),
      post: (p, body) => axios.post(`${base}${p}`, body, { headers }).then((r) => r.data).then(live(p)),
      put: (p, body) => axios.put(`${base}${p}`, body, { headers }).then((r) => r.data).then(live(p)),
      patch: (p, body) => axios.patch(`${base}${p}`, body, { headers }).then((r) => r.data).then(live(p)),
      del: (p) => axios.delete(`${base}${p}`, { headers }).then((r) => r.data).then(live(p)),
      blob: (p) => axios.get(`${base}${p}`, { headers, responseType: "blob" }).then((r) => r.data),
      upload: (p, formData) => axios.post(`${base}${p}`, formData, { headers: { ...headers, "Content-Type": "multipart/form-data" } }).then((r) => r.data).then(live(p)),
      blob: (p, params) => axios.get(`${base}${p}`, { headers, params, responseType: "blob" }).then((r) => r.data),
      postBlob: (p, body) => axios.post(`${base}${p}`, body, { headers, responseType: "blob" }).then((r) => ({ blob: r.data, headers: r.headers })),
      download: (p, params) => axios.get(`${base}${p}`, { headers, params, responseType: "blob" }).then((r) => {
        const fn = r.headers["x-filename"] || (/filename="?([^";]+)"?/.exec(r.headers["content-disposition"] || "") || [])[1] || "report.pdf";
        const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = fn; document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 30000);
        return fn;
      }),
      rawDownload: (p) => axios.get(`${API}/legacy${p}`, { headers, responseType: "blob" }).then((r) => {
        const fn = r.headers["x-filename"] || (/filename="?([^";]+)"?/.exec(r.headers["content-disposition"] || "") || [])[1] || "export.json.gz";
        const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = fn; document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 30000);
        return fn;
      }),
      raw: { get: (p, params) => axios.get(`${API}/legacy${p}`, { headers, params }).then((r) => r.data), post: (p, body) => axios.post(`${API}/legacy${p}`, body, { headers }).then((r) => r.data).then(live(p)),
        del: (p) => axios.delete(`${API}/legacy${p}`, { headers }).then((r) => r.data).then(live(p)), put: (p, body) => axios.put(`${API}/legacy${p}`, body, { headers }).then((r) => r.data).then(live(p)),
        upload: (p, formData) => axios.post(`${API}/legacy${p}`, formData, { headers: { ...headers, "Content-Type": "multipart/form-data" } }).then((r) => r.data).then(live(p)) },
    };
  }, [token, storeId]);
};

const key = (storeId, type) => `tp_${type}_shift_${storeId}`;

/* Mirrors the PHP session (last_shift_id / last_business_shift_id) using localStorage */
export const useShiftSession = (type) => {
  const api = useLegacyApi();
  const [shift, setShiftState] = useState(() => {
    const raw = localStorage.getItem(key(api.storeId, type));
    return raw ? JSON.parse(raw) : null;
  });
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const setShift = useCallback((s) => {
    setShiftState(s);
    if (s) localStorage.setItem(key(api.storeId, type), JSON.stringify(s));
    else localStorage.removeItem(key(api.storeId, type));
  }, [api.storeId, type]);

  useEffect(() => {
    if (!api.storeId) return;
    let alive = true;
    Promise.all([api.get("/settings"), api.get("/shifts/current")])
      .then(([st, cur]) => {
        if (!alive) return;
        setSettings(st);
        const stored = localStorage.getItem(key(api.storeId, type));
        if (stored) setShiftState((s) => s || JSON.parse(stored));
        else if (cur[type]) setShift(cur[type]);
      })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [api, type, setShift]);

  return { api, shift, setShift, settings, loading };
};

/* Autosave: call saveFn ~1.2 s after the snapshot (JSON of the form) changes. The first snapshot after a load becomes the baseline. */
export const useAutosave = (snapshot, enabled, saveFn, delay = 1200) => {
  const base = useRef(null);
  const [state, setState] = useState("idle");
  const reset = useCallback(() => { base.current = null; setState("idle"); }, []);
  useEffect(() => {
    if (!enabled) return;
    if (base.current === null) { base.current = snapshot; return; }
    if (snapshot === base.current) return;
    setState("pending");
    const t = setTimeout(async () => {
      setState("saving");
      try { await saveFn(); base.current = snapshot; setState("saved"); } catch { setState("error"); }
    }, delay);
    return () => clearTimeout(t);
  }, [snapshot, enabled, saveFn, delay]);
  return { state, reset };
};

export const AutosaveBadge = ({ state, testId = "autosave-state" }) => {
  if (state === "idle") return <span data-testid={testId} className="text-xs text-gray-400">Changes save automatically</span>;
  const map = { pending: ["text-gray-400", "Unsaved changes…"], saving: ["text-[#663399]", "Saving…"], saved: ["text-emerald-700", "✓ Saved"], error: ["text-red-600", "Could not save – check your connection"] };
  const [cls, label] = map[state];
  return <span data-testid={testId} className={`text-xs font-semibold ${cls}`}>{label}</span>;
};
